import moment from "moment";

import { getLocale, localisedMoment } from "features/common/localisation/utils";

type stringOrFn = string | (() => string);
type CalendarFormat = {
  sameDay: stringOrFn;
  nextDay: stringOrFn;
  lastDay: stringOrFn;
  nextWeek: stringOrFn;
  lastWeek: stringOrFn;
  sameElse: stringOrFn;
};

export function getRelativeDateAndTime(
  date: moment.Moment | string | number,
  referenceTime?: moment.Moment | string | number
) {
  return calendar(
    moment(date),
    referenceTime ? moment(referenceTime) : null,
    localiseDateAndTimeFormat
  );
}

export function getRelativeDate(
  date: moment.Moment | string | number,
  referenceTime?: moment.Moment | string | number
) {
  return calendar(
    moment(date),
    referenceTime ? moment(referenceTime) : null,
    localiseDateFormat
  );
}

export function getRelativeTime(date: moment.Moment | string | number) {
  return localisedMoment(date).format(localiseTimeFormat);
}

function calendar(
  date: moment.Moment,
  referenceTime: moment.Moment | null,
  formatFunction: (arg0: string, arg1: string) => CalendarFormat
) {
  const locale = getLocale();
  const fromNow = date.locale(locale).fromNow();
  const format = formatFunction(locale, fromNow);

  return localisedMoment(date).calendar(referenceTime || undefined, format);
}

const localiseTimeFormat = "LT";

function localiseDateFormat(locale: string, fromNow: string): CalendarFormat {
  switch (locale.toLowerCase()) {
    case "es":
    case "es-mx":
      return {
        sameDay: "[hoy]",
        nextDay: "[mañana]",
        lastDay: "[ayer]",
        nextWeek: `[${fromNow}]`,
        lastWeek: `[${fromNow}]`,
        sameElse: `[${fromNow}]`
      };

    case "pt-br":
      return {
        sameDay: "[Hoje]",
        nextDay: "[Amanhã]",
        lastDay: "[Ontem]",
        nextWeek: `[${fromNow}]`,
        lastWeek: `[${fromNow}]`,
        sameElse: `[${fromNow}]`
      };

    case "fr":
      return {
        lastDay: "[Hier]",
        sameDay: "[Ajourd'hui]",
        nextDay: "[Demain]",
        lastWeek: `[${fromNow}]`,
        nextWeek: `[${fromNow}]`,
        sameElse: `[${fromNow}]`
      };

    case "de-at":
    case "de":
      return {
        lastDay: "[Gestern] LT",
        sameDay: "[Heute] LT",
        nextDay: "[Morgen] LT",
        lastWeek: `[${fromNow}]`,
        nextWeek: `[${fromNow}]`,
        sameElse: `[${fromNow}]`
      };
    case "it":
      return {
        lastDay: "[Ieri]",
        sameDay: "[Oggi]",
        nextDay: "[Domani]",
        lastWeek: `[${fromNow}]`,
        nextWeek: `[${fromNow}]`,
        sameElse: `[${fromNow}]`
      };

    default:
      return {
        lastDay: "[Yesterday]",
        sameDay: "[Today]",
        nextDay: "[Tomorrow]",
        lastWeek: `[${fromNow}]`,
        nextWeek: `[${fromNow}]`,
        sameElse: `[${fromNow}]`
      };
  }
}

function localiseDateAndTimeFormat(
  locale: string,
  fromNow: string
): CalendarFormat {
  switch (locale.toLowerCase()) {
    case "es":
    case "es-mx":
      // @ts-ignore
      function showInDayEs() {
        // @ts-ignore
        return `[${fromNow}, a la${this.hours() !== 1 ? "s" : ""}] LT`;
      }

      return {
        sameDay: function () {
          // @ts-ignore
          return "[hoy, a la" + (this.hours() !== 1 ? "s" : "") + "] LT";
        },
        nextDay: function () {
          // @ts-ignore
          return "[mañana, a la" + (this.hours() !== 1 ? "s" : "") + "] LT";
        },
        lastDay: function () {
          // @ts-ignore
          return "[ayer, a la" + (this.hours() !== 1 ? "s" : "") + "] LT";
        },
        nextWeek: showInDayEs,
        lastWeek: showInDayEs,
        sameElse: showInDayEs
      };

    case "pt-br":
      const showInDaysPt = () => `[${fromNow}, às] LT`;

      return {
        sameDay: "[Hoje, às] LT",
        nextDay: "[Amanhã, às] LT",
        lastDay: "[Ontem, às] LT",
        nextWeek: showInDaysPt,
        lastWeek: showInDaysPt,
        sameElse: showInDaysPt
      };

    case "fr":
      const showInDaysFr = () => `[${fromNow}, à] LT`;

      return {
        lastDay: "[Hier, à] LT",
        sameDay: "[Ajourd'hui, à] LT",
        nextDay: "[Demain, à] LT",
        lastWeek: showInDaysFr,
        nextWeek: showInDaysFr,
        sameElse: showInDaysFr
      };

    case "de-at":
    case "de":
      const showInDaysDE = () => `[${fromNow}, um] LT`;
      return {
        lastDay: "[Gestern, um] LT",
        sameDay: "[Heute, um] LT",
        nextDay: "[Morgen, um] LT",
        lastWeek: showInDaysDE,
        nextWeek: showInDaysDE,
        sameElse: showInDaysDE
      };

    case "it":
      const showInDaysIT = () => `[${fromNow} alle] LT`;
      return {
        lastDay: "[Ieri alle] LT",
        sameDay: "[Oggi alle] LT",
        nextDay: "[Domani alle] LT",
        lastWeek: showInDaysIT,
        nextWeek: showInDaysIT,
        sameElse: showInDaysIT
      };

    default:
      const showInDays = () => `[${fromNow}, at] LT`;

      return {
        lastDay: "[Yesterday, at] LT",
        sameDay: "[Today, at] LT",
        nextDay: "[Tomorrow, at] LT",
        lastWeek: showInDays,
        nextWeek: showInDays,
        sameElse: showInDays
      };
  }
}
